import { useAssistantWebSocket } from '../../hooks/useAssistantWebSocket';
import { useAuthState } from '../../providers/AuthProvider';

import AssistantClientToolRunner from './AssistantClientToolRunner';
import AssistantPaneView from './AssistantPaneView';

/**
 * Top-level "Mantle Assistant" pane (route `/assistant`). Streams a
 * Claude-backed financial chat over WebSocket. Visibility is gated at the
 * route boundary by `feat-assistant` (`AssistantRouteGate` in `MainApp.tsx`);
 * this pane itself does not re-check that gate.
 *
 * Thin wrapper around {@link AssistantPaneView} — owns the hook, hands its
 * return shape to the presentational view. The split exists so Storybook
 * stories and visual-verification subagents can render the view from fixture
 * data without mocking the hook.
 *
 * Tool-call detail is not rendered. When the `debug-assistant` gate is on, the
 * server streams each tool's name / args / output on the live frames and the
 * hook logs them to the browser console as they complete.
 */
const AssistantPane = () => {
  const { session } = useAuthState();

  // Chat auth is cookie-based — pass a boolean instead of any credential.
  // The browser attaches `_s` to the WS upgrade automatically.
  const {
    messages,
    isProcessing,
    isAbortable,
    isRehydrating,
    connectionStatus,
    error,
    sendMessage,
    stopTurn,
    reclaim,
    pendingClientTools,
    sendClientToolResult,
  } = useAssistantWebSocket(session != null);

  // Run client tools one at a time — the head of the queue — so two browser
  // flows (e.g. two Plaid modals) never overlap. When the head reports its
  // result it leaves the queue and the next invocation takes over.
  const activeClientTool = pendingClientTools[0];

  return (
    <>
      <AssistantPaneView
        messages={messages}
        isProcessing={isProcessing}
        isAbortable={isAbortable}
        isRehydrating={isRehydrating}
        // A pending client tool means the turn is parked on the browser (the user
        // is finishing the Plaid flow) — suppress the "Thinking…" spinner.
        isAwaitingClientTool={activeClientTool !== undefined}
        connectionStatus={connectionStatus}
        error={error}
        onSend={sendMessage}
        onStop={stopTurn}
        onReclaim={reclaim}
      />
      {/* Runs a browser-side client tool (e.g. Plaid Link) when the model calls
          one, then reports the result. Keyed on the invocation so the next
          queued tool remounts and a server cancel unmounts + tears down. */}
      {activeClientTool && (
        <AssistantClientToolRunner
          key={activeClientTool.toolId}
          invocation={activeClientTool}
          onResult={sendClientToolResult}
        />
      )}
    </>
  );
};

export default AssistantPane;
